/** What `/sign-in` can be told by the server's redirects (apps/server/src/auth/routes.ts). */
export type SignInProblem = 'access_denied' | 'expired' | 'failed' | 'unavailable';

export interface SignInMessage {
  tone: 'info' | 'problem';
  title: string;
  body: string;
}

const PROBLEMS: Record<SignInProblem, SignInMessage> = {
  access_denied: {
    tone: 'problem',
    title: 'Sign-in was cancelled',
    body: 'Microsoft Entra ID didn’t finish signing you in. You can try again whenever you’re ready.',
  },
  expired: {
    tone: 'problem',
    title: 'That sign-in took too long',
    body: 'For your security, a sign-in only lasts a few minutes. Start again and you’ll come straight back here.',
  },
  failed: {
    tone: 'problem',
    title: 'We couldn’t sign you in',
    body: 'Something didn’t check out with that sign-in. Try again, and if it keeps happening, contact the Stock Admin team.',
  },
  unavailable: {
    tone: 'problem',
    title: 'Microsoft Entra ID isn’t answering',
    body: 'We couldn’t reach the sign-in service just now. Wait a moment and try again.',
  },
};

const SIGNED_OUT: SignInMessage = {
  tone: 'info',
  title: 'You’re signed out',
  body: 'Thanks for stopping by. Sign in again whenever you need MyRewards.',
};

function isProblem(value: string | null): value is SignInProblem {
  return value !== null && Object.hasOwn(PROBLEMS, value);
}

/**
 * The calm message for `?problem=…` or `?signedOut=1`, or `null`. Unknown problem codes get the
 * generic "We couldn't sign you in", never the raw code.
 */
export function signInMessage(params: URLSearchParams): SignInMessage | null {
  const problem = params.get('problem');
  if (problem !== null) return isProblem(problem) ? PROBLEMS[problem] : PROBLEMS.failed;
  if (params.get('signedOut') === '1') return SIGNED_OUT;
  return null;
}

/** Why `/no-access` was reached (the server's `?reason=`). */
export type NoAccessReason = 'not_in_directory' | 'inactive' | 'waiting_for_admin';

export interface NoAccessCopy {
  title: string;
  body: string;
  next: string;
}

const NO_ACCESS: Record<NoAccessReason, NoAccessCopy> = {
  not_in_directory: {
    title: 'MyRewards doesn’t have you yet',
    body: 'You signed in with Microsoft Entra ID, but your account isn’t connected to a MyRewards record yet. The System Admins can see that you tried. New starters usually appear after the next directory update.',
    next: 'If you’ve been at Areté a while, ask the Stock Admin team to check your record.',
  },
  waiting_for_admin: {
    title: 'We found a record that may be yours',
    body: 'MyRewards has a record that looks like you, so it didn’t make a second one. A System Admin will connect it to your Entra account, then you can sign in.',
    next: 'If it’s urgent, ask the Stock Admin team.',
  },
  inactive: {
    title: 'Your MyRewards access is off',
    body: 'The directory shows this account as no longer active, so MyRewards is closed to it. Your awards and letters are kept.',
    next: 'If that’s wrong, ask the Stock Admin team to check your directory record.',
  },
};

const NO_ACCESS_DEFAULT: NoAccessCopy = {
  title: 'You don’t have access to MyRewards',
  body: 'You signed in, but this account can’t use MyRewards.',
  next: 'If you think you should have access, ask the Stock Admin team.',
};

export function noAccessCopy(reason: string | null): NoAccessCopy {
  return reason !== null && Object.hasOwn(NO_ACCESS, reason)
    ? NO_ACCESS[reason as NoAccessReason]
    : NO_ACCESS_DEFAULT;
}
